import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import {
  PULSE_EMPTY_TAB_DATA,
  PULSE_COUNTED_TAB_CATEGORIES,
  PULSE_OVERVIEW_ERROR_MESSAGE,
} from "../../../constants/pulse.constants";
import {
  PulseContextType,
  PulseCountedTabCategory,
  PulseOverviewData,
  PulseProviderProps,
  PulseTabStatusMap,
  PulseTabKey,
} from "../../../interfaces/pulse.interfaces";
import { PulseRestS } from "../../../rest/pulse-rest-service";

export const PulseContext = createContext<PulseContextType>(null);

// react-final-form convention for any tab with a real form/grid (e.g. the reused
// DebtScheduleGrid/ShareholderGrid, or a new Bank Statement upload form):
//   - initialValues comes from usePulseContext().tabData[tabKey] — never re-fetched
//     just to seed a form, since PulseContext already holds it.
//   - onSubmit calls that tab's own REST endpoint, then on success calls
//     updateTabData(tabKey, (prev) => ...) to write the result back into the cache
//     (and dispatches the sidebar-badge Redux update where relevant, once that
//     slice exists).
//   - A Field/FieldArray never calls updateTabData directly — only onSubmit does,
//     same as Underwriting: react-final-form owns in-progress edits, the cache
//     only receives the committed result.
export const usePulseContext = (): PulseContextType => {
  const context = useContext(PulseContext);

  if (context === null) {
    throw new Error("usePulseContext must be used within a PulseProvider");
  }

  return context;
};

// Stub — replaced with a real per-tab REST call (RestS/pulse-rest-service) as each tab is built.
const fetchTabDataFromApi = async (tabKey: PulseTabKey): Promise<any> => {
  // TODO: wire real calls per tab (connections, gst, bankStatements, books, financials, debtInvestments)
  return null;
};

const PulseProvider = ({ children }: PulseProviderProps) => {
  const navigate = useNavigate();
  // null while checking; Pulse renders and loads only once access is confirmed.
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<PulseOverviewData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [completedTabActions, setCompletedTabActions] = useState<
    Record<PulseCountedTabCategory, number>
  >({
    connections: 0,
    gst: 0,
    bankStatements: 0,
    books: 0,
    financials: 0,
    investments: 0,
  });
  const [tabData, setTabData] = useState<Record<PulseTabKey, any | null>>(
    PULSE_EMPTY_TAB_DATA
  );

  useEffect(() => {
    let isMounted = true;

    const loadSummary = () =>
      PulseRestS.getOverview()
      .then((data) => {
        if (!isMounted) return;
        setSummary(data);
        setSummaryError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        const message =
          error instanceof Error ? error.message : PULSE_OVERVIEW_ERROR_MESSAGE;
        setSummary(null);
        setSummaryError(message);
        toast.error(message);
      })
      .finally(() => {
        if (isMounted) setSummaryLoading(false);
      });

    // Same guard as UnderwritingHome: no access → 404, replacing history.
    PulseRestS.checkAccess().then((allowed) => {
      if (!isMounted) return;
      if (!allowed) {
        navigate("/404", { replace: true });
        return;
      }
      setHasAccess(true);
      loadSummary();
    });

    // Runs once on mount — this Provider is mounted at the /pulse/* route boundary
    // (App.tsx), so it survives tab switching and only remounts if the user leaves
    // /pulse entirely, same lifecycle as UnderWritingFormProvider.

    return () => {
      isMounted = false;
    };
  }, []);

  // Count plus which overview bucket the category sits in, so the tab badge
  // can colour itself the way the overview cards do.
  const tabStatus = useMemo<PulseTabStatusMap | null>(() => {
    if (!summary) return null;

    return PULSE_COUNTED_TAB_CATEGORIES.reduce((statuses, category) => {
      const bucket = summary.needActions?.[category]
        ? "needActions"
        : summary.dueToday?.[category]
          ? "dueToday"
          : "allSet";
      const apiCount = summary[bucket]?.[category]?.pendingActions ?? 0;
      statuses[category] = {
        bucket,
        pendingActions: Math.max(0, apiCount - completedTabActions[category]),
      };
      return statuses;
    }, {} as PulseTabStatusMap);
  }, [completedTabActions, summary]);

  const completeTabAction = useCallback(
    (category: PulseCountedTabCategory) => {
      setCompletedTabActions((current) => ({
        ...current,
        [category]: current[category] + 1,
      }));
    },
    []
  );

  const fetchTabData = async (tabKey: PulseTabKey) => {
    if (tabData[tabKey] !== null) {
      // already cached — no-op, this is what avoids refetching on tab revisit
      return;
    }

    const data = await fetchTabDataFromApi(tabKey);
    setTabData((prev) => ({ ...prev, [tabKey]: data }));
  };

  const updateTabData = (tabKey: PulseTabKey, updater: (prev: any) => any) => {
    setTabData((prev) => ({ ...prev, [tabKey]: updater(prev[tabKey]) }));
  };

  const value: PulseContextType = {
    summary,
    summaryLoading,
    summaryError,
    tabStatus,
    completeTabAction,
    tabData,
    fetchTabData,
    updateTabData,
  };

  if (!hasAccess) return <CircularLoader />;

  return <PulseContext.Provider value={value}>{children}</PulseContext.Provider>;
};

export default PulseProvider;
